import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X, ImagePlus, Loader2 } from "lucide-react";
import api from "@/api";

const inputCls = "rounded-lg border border-clayline bg-cream px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const emptyForm = { name: "", price: "", size: "", description: "", badge: "", image: "" };
const imgSrc = (p) => (!p ? "" : p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`);

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    api.get("/products").then((r) => setProducts(r.data)).catch(() => toast.error("Failed to load products"));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (p) => {
    setEditing(p.id);
    setAdding(false);
    setForm({
      name: p.name,
      price: String(p.price),
      size: p.size || "",
      description: p.description || "",
      badge: p.badge || "",
      image: p.image || "",
    });
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim() || form.price === "") {
      toast.error("Name and price are required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      price: parseFloat(form.price),
      size: form.size.trim(),
      description: form.description.trim(),
      badge: form.badge,
      image: form.image,
    };
    try {
      if (adding) await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing}`, payload);
      toast.success(adding ? "Product added to the shop" : "Product updated");
      setEditing(null);
      setAdding(false);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Save failed. Please try again.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the shop?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Delete failed. Please try again.");
    }
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setForm(emptyForm);
  };

  const editForm = (
    <div className="bg-sand/60 border border-terracotta/50 rounded-xl p-4 grid sm:grid-cols-2 gap-3" data-testid="product-edit-form">
      <input placeholder="Product name" value={form.name} onChange={set("name")} className={inputCls} data-testid="product-form-name" />
      <input placeholder="Price ₹" value={form.price} onChange={set("price")} type="number" min="0" className={inputCls} data-testid="product-form-price" />
      <input placeholder="Size / pack (e.g. 250g, Set of 6)" value={form.size} onChange={set("size")} className={inputCls} data-testid="product-form-size" />
      <select value={form.badge} onChange={set("badge")} className={inputCls} data-testid="product-form-badge">
        <option value="">No badge</option>
        <option value="bestseller">Bestseller</option>
        <option value="limited">Limited</option>
      </select>
      <input placeholder="Description" value={form.description} onChange={set("description")} className={`${inputCls} sm:col-span-2`} data-testid="product-form-description" />
      <div className="sm:col-span-2 flex items-center gap-4">
        {form.image ? (
          <div className="flex items-center gap-3">
            <img src={imgSrc(form.image)} alt="Product" className="w-16 h-16 rounded-xl object-cover border border-clayline" data-testid="product-form-image-preview" />
            <button type="button" onClick={() => setForm({ ...form, image: "" })} data-testid="product-form-image-remove" className="font-mono text-[11px] uppercase tracking-wider text-red-600 hover:underline">
              Remove
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-dashed border-terracotta text-terracotta cursor-pointer hover:bg-terracotta hover:text-cream transition-colors" data-testid="product-form-image-upload">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? "Uploading…" : "Add Photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} />
          </label>
        )}
      </div>
      <div className="sm:col-span-2 flex gap-3">
        <button onClick={save} data-testid="product-save-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center gap-2">
          <Check size={14} /> Save
        </button>
        <button onClick={cancel} data-testid="product-cancel-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-clayline hover:border-charcoal transition-colors inline-flex items-center gap-2">
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="product-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">{products.length} products</p>
        <button
          onClick={() => { setAdding(true); setEditing(null); setForm(emptyForm); }}
          data-testid="product-add-btn"
          className="rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-charcoal text-cream hover:bg-terracotta transition-colors inline-flex items-center gap-2"
        >
          <Plus size={14} /> Add Product
        </button>
      </div>
      {adding && <div className="mb-6">{editForm}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        {products.map((p) => (
          editing === p.id ? (
            <div key={p.id} className="md:col-span-2">{editForm}</div>
          ) : (
            <div key={p.id} className="bg-cream border border-clayline rounded-2xl p-5 flex items-start gap-4 group" data-testid={`product-row-${p.id.slice(0, 8)}`}>
              {p.image ? (
                <img src={imgSrc(p.image)} alt={p.name} className="w-16 h-16 rounded-xl object-cover border border-clayline shrink-0" />
              ) : (
                <span className="w-16 h-16 rounded-xl border border-dashed border-clayline shrink-0 flex items-center justify-center text-charcoal/25"><ImagePlus size={18} /></span>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-serif font-bold text-lg">{p.name}</h3>
                  {p.badge && <span className="font-mono text-[10px] uppercase tracking-wider bg-terracotta text-cream px-2.5 py-0.5 rounded-full">{p.badge}</span>}
                </div>
                <p className="font-mono text-sm text-terracotta mt-1">₹{p.price} <span className="text-charcoal/40">· {p.size}</span></p>
                {p.description && <p className="text-xs text-charcoal/55 mt-1.5 line-clamp-2">{p.description}</p>}
              </div>
              <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => startEdit(p)} data-testid={`product-edit-${p.id.slice(0, 8)}`} className="p-2 hover:text-terracotta transition-colors" aria-label={`Edit ${p.name}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(p)} data-testid={`product-delete-${p.id.slice(0, 8)}`} className="p-2 hover:text-red-600 transition-colors" aria-label={`Delete ${p.name}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default ProductManager;
